import { createContext, ReactElement } from 'react';

// project-imports
import useLocalStorage from 'hooks/useLocalStorage';
import config, { MenuOrientation, ThemeDirection, ThemeMode } from 'config';

// types
import { CustomizationProps, I18n } from 'types/config';

interface PersistedPrefs {
  mode: ThemeMode;
  i18n: I18n;
}

const defaultPrefs: PersistedPrefs = {
  mode: config.mode,
  i18n: config.i18n, // 'es' por defecto (ver config.ts)
};

// initial state
const initialState: CustomizationProps = {
  ...config,
  onChangeLocalization: () => {},
  onChangeMenuOrientation: () => {},
  onChangeDirection: () => {},
  onChangeContainer: () => {},
  onChangeCaption: () => {},
  onChangeSideTheme: () => {},
  onChangeThemePreset: () => {},
  onChangeMenuIcon: () => {},
  onChangeMode: () => {},
  onReset: () => {}
};

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactElement;
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

function ConfigProvider({ children }: ConfigProviderProps) {
  const [prefs, setPrefs] = useLocalStorage<PersistedPrefs>('sx-app', defaultPrefs);

  const cfg = { ...initialState, mode: prefs.mode, i18n: prefs.i18n };

  const onChangeMode = (selectedMode: ThemeMode) => {
    setPrefs({ ...prefs, mode: selectedMode });
  };

  const onChangeLocalization = (lang: I18n) => {
    setPrefs({ ...prefs, i18n: lang });
  };

  const onReset = () => setPrefs(defaultPrefs);

  const onChangeMenuOrientation = (_layout: MenuOrientation) => {};
  const onChangeCaption = (_caption: boolean) => {};
  const onChangeSideTheme = (_sidebarTheme: boolean) => {};
  const onChangeDirection = (_direction: ThemeDirection) => {};
  const onChangeContainer = (_container: boolean) => {};
  const onChangeThemePreset = (_key: string, _value: string) => {};
  const onChangeMenuIcon = (_key: string, _value: string) => {};

  return (
    <ConfigContext
      value={{
        ...cfg,
        onChangeLocalization,
        onChangeMenuOrientation,
        onChangeMode,
        onChangeDirection,
        onChangeContainer,
        onChangeCaption,
        onChangeSideTheme,
        onChangeThemePreset,
        onChangeMenuIcon,
        onReset
      }}
    >
      {children}
    </ConfigContext>
  );
}

export { ConfigProvider, ConfigContext };
